import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { Box, Button, Typography, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DynamicTable from '../../components/Table/DataTable';
import { IconCopy, IconDelete, IconEye } from '../../components/icons/icons';
import LoadingScreen from '../../components/LoadingScreen';
import { deleteCoupon, getCoupons } from '../../services/woocommerce';
import { Plus } from 'lucide-react';
import ErrorComponent from '../../components/ErrorComponent';

const CouponAdminPage = () => {
  const [coupons, setCoupons] = useState<any[]>([]);
  console.log("coupons", coupons);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await getCoupons();
      const items = response.data || [];
      setCoupons(items);
      setTotalCount(items.length);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const isExpired = (date: string | null | undefined) => {
    if (!date) return false;
    return new Date(date).getTime() < Date.now();
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  };

  const columns = [
    {
      key: 'serial',
      label: 'S.No',
      align: 'center',
      render: (_row: any, index: number) => page * rowsPerPage + index + 1,
    },
    {
      key: 'amountLimits',
      label: 'Amount Limits',
      align: 'center',
      render: (row: any) => (
        <div className="py-1 space-y-1 w-full max-w-[100px]">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">Minimum</span>
            <span className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded">
              {row.minimum_amount ? `₹${row.minimum_amount}` : '-'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">Maximum</span>
            <span className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded">
              {row.maximum_amount ? `₹${row.maximum_amount}` : '-'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'usageLimits',
      label: 'Usage Limits',
      align: 'center',
      render: (row: any) => (
        <div className="py-1 space-y-1 w-full max-w-[100px]">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">Usage Limit</span>
            <span className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded">
              {row.usage_limit || '-'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">Usage Count</span>
            <span className="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded">
              {row.usage_count || '0'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Coupon Code',
      align: 'center',
      render: (row: any) => (
        <span className="px-2 py-0.5 text-xs font-bold uppercase bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-full shadow-sm border border-blue-200">
          {row?.code || '-'}
        </span>
      ),
    },
    
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (row: any) =>
        isExpired(row.date_expires) ? (
          <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-red-100 to-red-200 text-red-800 rounded-full shadow-sm border border-red-200">
            Expired
          </span>
        ) : (
          <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-green-100 to-green-200 text-green-800 rounded-full shadow-sm border border-green-200">
            Active
          </span>
        ),
    },
  ];

  const actions = [
    {
      label: 'View',
      color: '#2196f3',
      icon: <IconEye size={20} />,
      onClick: (row: any) => navigate(`/coupons/${row.id}`),
    },
    {
      label: 'Copy',
      color: '#4f46e5',
      icon: <IconCopy size={20} />,
      onClick: (row: any) => {
        if (row?.code) {
          handleCopy(row.code);
        }
      },
    },
    {
      label: 'Delete',
      color: '#dc2626',
      icon: <IconDelete size={20} />,
      onClick: async (row: any) => {
        if (!window.confirm("Are you sure you want to delete this coupon?")) return;
        try {
          await deleteCoupon(row.id, true);
          alert("Coupon deleted successfully");
          fetchCoupons();
        } catch (err: any) {
          console.error("Failed to delete coupon:", err);
          alert("Failed to delete coupon");
        }
      },
    },
  ];

  if (loading) return <LoadingScreen message="Loading Coupons..." layout={false} />;
  if (error) return <ErrorComponent error={error} onRetry={fetchCoupons} />;

  return (
    <>
      <Box p={2}>
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "black",
              "&:hover": { backgroundColor: "#333" },
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            onClick={() => navigate("/coupons/new")}
            startIcon={<Plus size={18} />}
          >
            Add Coupon
          </Button>
        </Box>
        <DynamicTable
          columns={columns}
          data={coupons}
          loading={loading}
          actions={actions}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          totalCount={totalCount}
        />
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default CouponAdminPage;